/**
 * Filename rules a step's editor previews with, so it stays importable from a
 * browser bundle; `captureUtils` reaches for `fs`, `os` and `sharp`.
 */

import type { Expression } from '../../types';

import type { FileNamePart } from './captureConfig';

/** Anything outside this is collapsed before it reaches a filename. */
export const UNSAFE_FILENAME_CHARS = /[^\w.-]+/g;

/** What a preview shows for a value, such as `<step_number>`. */
const PLACEHOLDER = /(<[^<>]+>)/;

/**
 * How long a composed name may run. A path component caps at 255 bytes on the
 * filesystems we ship on, and the record beside a frame adds `_metadata.json`.
 */
const MAX_FILE_NAME_CHARS = 200;

export type CapturingStepKind =
  | 'Capture'
  | 'Locate'
  | 'RunLocateSkill'
  | 'RunInspectSkill';

/**
 * What each capturing step is called, in a filename and in a directory name.
 *
 * One map for both, so `captures/inspect/` and an `inspect_…png` on an FTP
 * server cannot come to disagree about what an Inspect step is called.
 */
export const CAPTURE_FILENAME_PREFIX: Record<CapturingStepKind, string> = {
  Capture: 'capture',
  Locate: 'locate',
  RunLocateSkill: 'locate',
  RunInspectSkill: 'inspect',
};

/** The keys a step number and step name take among a capture's named values. */
export const NAME_VALUE_KEYS = {
  stepNumber: 'step_number',
  stepName: 'step_name',
} as const;

/** `seat number` and `seatNumber` are both `seat_number`. */
export function nameValueKey(name: string): string {
  return name
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

/**
 * A text part split at its `{name}` references: literal text at even indexes,
 * the routine variables it names at odd ones.
 */
export function splitTextVariables(text: string): string[] {
  return text
    .split(/\{([^{}]+)\}/)
    .map((piece, index) => (index % 2 === 1 ? piece.trim() : piece));
}

/**
 * How a step reads each kind of part: the robot's values when it runs, and
 * placeholders in its editor's preview.
 */
export interface FileNamePartValues {
  stepNumber(): string;
  stepName(): string;
  variable(variableID: string): string;
  stepOutput(stepID: string, name: string): string;
  /** A `{name}` in a text part, which names a routine variable. */
  textVariable(name: string): string;
  /** The text around a text part's `{name}`s, as the operator typed it. */
  text(literal: string): string;
}

/** Each part's text, in order, before it is made safe for a filename. */
export function fileNamePartTexts(
  fileName: FileNamePart[],
  values: FileNamePartValues,
): string[] {
  return fileName.map((part) => {
    switch (part.kind) {
      case 'stepNumber':
        return `Step_${values.stepNumber()}`;
      case 'stepName':
        return values.stepName();
      case 'variable':
        return values.variable(part.variableID);
      case 'stepVariable':
        return values.stepOutput(part.stepID, part.name);
      case 'text':
        return splitTextVariables(part.value)
          .map((piece, index) =>
            index % 2 === 1 ? values.textVariable(piece) : values.text(piece),
          )
          .join('');
      default:
        return part satisfies never;
    }
  });
}

/**
 * One part of a filename, made safe to join onto a directory or base path. A
 * preview keeps its placeholders whole, so its own text must already be safe.
 */
export function filenameSafePart(
  part: string,
  keepPlaceholders = false,
): string {
  return (keepPlaceholders ? part.split(PLACEHOLDER) : [part])
    .map((piece, index) =>
      index % 2 === 1 ? piece : piece.replace(UNSAFE_FILENAME_CHARS, '_'),
    )
    .join('')
    .replace(/^_+|_+$/g, '');
}

/**
 * What a Capture step saves when no expression names its file. Builds older
 * than the file name parts require the field, so a routine saved with it still
 * loads on them.
 */
export const EMPTY_SAVE_FILEPATH: Expression = { kind: 'constant', value: '' };

/**
 * The expression a Capture step names its file with, if any. An empty one, as
 * the editor saves on every step without one, names nothing, and the file name
 * parts apply.
 */
export function namingExpression(
  saveFilepath: Expression | undefined,
): Expression | undefined {
  return saveFilepath?.kind === 'constant' && saveFilepath.value === ''
    ? undefined
    : saveFilepath;
}

/**
 * Without the extension. A preview is not cut short, since its placeholders
 * are not the lengths of the values they stand for.
 */
export function captureFileName(
  parts: string[],
  keepPlaceholders = false,
): string {
  const name = parts
    .map((part) => filenameSafePart(part, keepPlaceholders))
    .filter(Boolean)
    .join('_');

  return keepPlaceholders ? name : name.slice(0, MAX_FILE_NAME_CHARS);
}

/** The name a capture takes without a `saveFilepath`, before the extension. */
export function generatedCaptureName(args: {
  stepKind: CapturingStepKind;
  skillId?: string;
  /** The `captureTimestamp` published to the routine. */
  timestamp: string;
  parts?: string[];
  /** For a preview, whose skill, timestamp and values are placeholders. */
  keepPlaceholders?: boolean;
}): string {
  const { stepKind, skillId, timestamp, parts = [], keepPlaceholders } = args;

  return captureFileName(
    [CAPTURE_FILENAME_PREFIX[stepKind], skillId ?? '', timestamp, ...parts],
    keepPlaceholders,
  );
}
